import apiClient from './apiClient';
import config from '../utils/config';

const apiService = {
  /**
   * Get all API endpoints for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the list of API endpoints
   */
  getApiEndpoints: async (projectId) => {
    try {
      const response = await apiClient.get(config.PROJECT_ENDPOINTS.API_ENDPOINTS.ALL(projectId));
      console.log('Raw endpoint response:', response);
      
      // Extract endpoints based on the actual response structure
      // Since apiClient already returns response.data, we now have the direct response
      if (response && response.data && response.data.endpoints) {
        return response.data.endpoints;
      } else if (response && response.data && Array.isArray(response.data)) {
        return response.data;
      } else if (Array.isArray(response)) {
        return response;
      }
      
      console.warn('Endpoints data structure not as expected:', response);
      return [];
    } catch (error) {
      console.error('Error fetching endpoints:', error);
      return [];
    }
  },

  /**
   * Create a new API endpoint
   * @param {string} projectId - Project ID
   * @param {Object} data - API endpoint data
   * @returns {Promise} Promise resolving to the created API endpoint
   */
  createApiEndpoint: async (projectId, data) => {
    try {
      const response = await apiClient.post(config.PROJECT_ENDPOINTS.API_ENDPOINTS.ALL(projectId), data);
      console.log('Create endpoint response:', response);
      
      if (response && response.data && response.data.endpoint) {
        return response.data.endpoint;
      }
      return response;
    } catch (error) {
      console.error('Error creating endpoint:', error);
      throw error;
    }
  },

  /**
   * Update an API endpoint
   * @param {string} projectId - Project ID
   * @param {string} endpointId - API endpoint ID
   * @param {Object} data - Updated API endpoint data
   * @returns {Promise} Promise resolving to the updated API endpoint
   */
  updateApiEndpoint: async (projectId, endpointId, data) => {
    try {
      const response = await apiClient.patch(
        config.PROJECT_ENDPOINTS.API_ENDPOINTS.DETAILS(projectId, endpointId),
        data
      );
      console.log('Update endpoint response:', response);
      
      if (response && response.data && response.data.endpoint) {
        return response.data.endpoint;
      }
      return response;
    } catch (error) {
      console.error('Error updating endpoint:', error);
      throw error;
    }
  },

  /**
   * Delete an API endpoint
   * @param {string} projectId - Project ID
   * @param {string} endpointId - API endpoint ID
   * @returns {Promise} Promise resolving to the deletion status
   */
  deleteApiEndpoint: async (projectId, endpointId) => {
    try {
      const response = await apiClient.delete(
        config.PROJECT_ENDPOINTS.API_ENDPOINTS.DETAILS(projectId, endpointId)
      );
      return response;
    } catch (error) {
      console.error('Error deleting endpoint:', error);
      throw error;
    }
  },

  /**
   * Test an API endpoint
   * @param {string} projectId - Project ID
   * @param {string} endpointId - API endpoint ID
   * @param {Object} testData - Test data for the endpoint
   * @returns {Promise} Promise resolving to the test results
   */
  testApiEndpoint: async (projectId, endpointId, testData) => {
    try {
      const response = await apiClient.post(
        config.PROJECT_ENDPOINTS.API_ENDPOINTS.TEST(projectId, endpointId),
        testData
      );
      console.log('Test endpoint response:', response);
      
      if (response && response.data && response.data.testResults) {
        return response.data.testResults;
      }
      return response;
    } catch (error) {
      console.error('Error testing endpoint:', error);
      throw error;
    }
  },

  /**
   * Generate a response example for an API endpoint
   * @param {string} projectId - Project ID
   * @param {string} endpointId - API endpoint ID
   * @param {Object} parameters - Optional parameters to use for the request
   * @returns {Promise} Promise resolving to the generated example response
   */
  generateResponseExample: async (projectId, endpointId, parameters = {}) => {
    try {
      console.log(`Generating response example for endpoint ${endpointId} in project ${projectId}`);
      const response = await apiClient.post(
        config.PROJECT_ENDPOINTS.API_ENDPOINTS.GENERATE_EXAMPLE(projectId, endpointId),
        parameters
      );
      console.log('Generate example response:', response);
      
      if (response && response.data) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('Error generating response example:', error);
      throw error;
    }
  },

  /**
   * Get project settings
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the project settings
   */
  getProjectSettings: async (projectId) => {
    try {
      console.log('Fetching project settings for ID:', projectId);
      const response = await apiClient.get(
        config.PROJECT_ENDPOINTS.SETTINGS(projectId)
      );
      console.log('Project settings raw response:', response);
      
      if (response && response.data && response.data.settings) {
        const settings = response.data.settings;
        console.log('Extracted settings object:', settings);
        
        // Check Google Maps settings specifically
        if (settings.externalServices && settings.externalServices.googleMaps) {
          console.log('Google Maps settings found:', settings.externalServices.googleMaps);
          console.log('Enabled status:', settings.externalServices.googleMaps.enabled);
          console.log('API Key present:', settings.externalServices.googleMaps.apiKey ? 'Yes' : 'No');
        } else {
          console.log('No Google Maps settings found in response');
        }
        
        return settings;
      }
      
      console.log('Unexpected response format, returning raw response:', response);
      return response;
    } catch (error) {
      console.error('Error fetching settings:', error);
      return {};
    }
  },

  /**
   * Update project settings
   * @param {string} projectId - Project ID
   * @param {Object} data - Updated settings data
   * @returns {Promise} Promise resolving to the updated settings
   */
  updateProjectSettings: async (projectId, data) => {
    try {
      const response = await apiClient.patch(
        config.PROJECT_ENDPOINTS.SETTINGS(projectId),
        data
      );
      console.log('Update settings response:', response);
      
      if (response && response.data && response.data.settings) {
        return response.data.settings;
      }
      return response;
    } catch (error) {
      console.error('Error updating settings:', error);
      throw error;
    }
  },

  /**
   * Sync API endpoint to production
   * @param {string} projectId - Project ID
   * @param {string} endpointId - API endpoint ID
   * @returns {Promise} Promise resolving to the sync results
   */
  syncApiEndpointToProduction: async (projectId, endpointId) => {
    try {
      console.log(`Syncing API endpoint ${endpointId} to production for project ${projectId}`);
      const response = await apiClient.post(
        config.PROJECT_ENDPOINTS.API_ENDPOINTS.SYNC_TO_PRODUCTION(projectId, endpointId)
      );
      console.log('Sync API endpoint response:', response);
      
      if (response && response.data) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('Error syncing API endpoint to production:', error);
      throw error;
    }
  },

  /**
   * Sync project to production
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to the sync results
   */
  syncProjectToProduction: async (projectId) => {
    try {
      console.log(`Syncing project ${projectId} to production`);
      const response = await apiClient.post(
        config.PROJECT_ENDPOINTS.SYNC_TO_PRODUCTION(projectId)
      );
      console.log('Sync project response:', response);
      
      if (response && response.data) {
        return response.data;
      }
      return response;
    } catch (error) {
      console.error('Error syncing project to production:', error);
      throw error;
    }
  },

  /**
   * Get quota status for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to quota status
   */
  getQuotaStatus: async (projectId) => {
    try {
      const response = await apiClient.get(`/projects/${projectId}/quota/status`);
      return response;
    } catch (error) {
      console.error('Error fetching quota status:', error);
      throw error;
    }
  },

  /**
   * Top up quota for a project
   * @param {string} projectId - Project ID
   * @param {Object} data - Top up data (amount, reason)
   * @returns {Promise} Promise resolving to top up result
   */
  topUpQuota: async (projectId, data) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/quota/topup`, data);
      return response;
    } catch (error) {
      console.error('Error topping up quota:', error);
      throw error;
    }
  },

  /**
   * Reset quota for a project
   * @param {string} projectId - Project ID
   * @returns {Promise} Promise resolving to reset result
   */
  resetQuota: async (projectId) => {
    try {
      const response = await apiClient.post(`/projects/${projectId}/quota/reset`);
      return response;
    } catch (error) {
      console.error('Error resetting quota:', error);
      throw error;
    }
  },
};

export default apiService; 